/**
 * The window the home sales dashboard is asked for.
 *
 * One period governs the whole block — the funnel, closed-per-month and losses
 * all come from a single payload — so it lives here rather than inside any one
 * card, and the control that sets it belongs to whichever card heads the block.
 *
 * Calendar years lead the list on purpose. A rolling window is the natural way
 * to *implement* a range and the wrong way to *read* one: a chart labelled
 * "sept 25 → ago 26" straddles two years, so no month on it answers "how are we
 * doing this year". Years are what people close books on.
 *
 * Every option resolves to explicit `from`/`to` dates, which is what the
 * endpoint takes. It also accepts a trailing length in months and the two agree,
 * but a length always ends today — a closed year cannot be said that way at all.
 * The API caps a window at 24 calendar months; a single year is 12.
 */
import { DateTime } from 'luxon';

export interface DashboardPeriodOption {
  /** i18n key, or a literal year — a missing key renders as itself, and "2025"
   *  needs no translating. */
  label: string;
  value: string;
}

export interface DashboardPeriodRange {
  /** Inclusive, `YYYY-MM-DD`. */
  from: string;
  /** Inclusive, `YYYY-MM-DD`. */
  to: string;
}

/** Year to date: what the dashboard opens on. */
export const DEFAULT_DASHBOARD_PERIOD = 'ytd';

/** How many closed years are offered behind the current one. */
const PAST_YEARS = 2;

/**
 * The pickable periods.
 *
 * Two families, and the list is ordered so that both read the same way — start
 * at today and reach further back:
 *
 *  1. **Calendar years**, newest first: the current one, then each closed year.
 *     They lead because they are the frame people close books on.
 *  2. **Rolling windows**, narrowest first: three months, then six, then twelve.
 *
 * The families stay contiguous because the select renders a flat list with no
 * group headings — the order is the only structure available, so it has to
 * carry it. Before this the years ran newest-to-oldest while the rolling
 * windows ran widest-to-narrowest, two opposite directions in one menu.
 *
 * The past years are computed rather than listed so the option set never goes
 * stale, and they carry their own number as the label — "2025" reads without
 * arithmetic where "año anterior" makes the reader do it.
 *
 * @param now Injectable clock, so a test can pin the year.
 */
export function dashboardPeriodOptions(now: Date = new Date()): DashboardPeriodOption[] {
  const year = now.getFullYear();
  const years = Array.from({ length: PAST_YEARS }, (_, index) => year - (index + 1));

  return [
    { label: 'PERIOD_CURRENT_YEAR', value: DEFAULT_DASHBOARD_PERIOD },
    ...years.map((value) => ({ label: `${value}`, value: `y${value}` })),
    { label: 'LAST_3_MONTHS', value: 'm3' },
    { label: 'LAST_6_MONTHS', value: 'm6' },
    { label: 'LAST_12_MONTHS', value: 'm12' },
  ];
}

/** `YYYY-MM-DD` from the date's own calendar fields. Not `toISOString`, which
 *  converts to UTC first and hands back the previous day west of Greenwich. */
function isoDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** First day of the month `back` months before this one. */
function monthStart(now: Date, back: number): Date {
  return new Date(now.getFullYear(), now.getMonth() - back, 1);
}

/**
 * The window a period id stands for.
 *
 * @param value A `DashboardPeriodOption.value`; anything unrecognised falls back to year to date.
 * @param now Injectable clock, so a test can pin the date.
 */
export function dashboardPeriodRange(value: string, now: Date = new Date()): DashboardPeriodRange {
  const today = isoDay(now);

  if (value?.startsWith('y')) {
    const year = Number(value.slice(1));
    if (Number.isInteger(year)) {
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    }
  }

  switch (value) {
    case 'm3':
      return { from: isoDay(monthStart(now, 2)), to: today };
    case 'm6':
      return { from: isoDay(monthStart(now, 5)), to: today };
    case 'm12':
      return { from: isoDay(monthStart(now, 11)), to: today };
    default:
      return { from: `${now.getFullYear()}-01-01`, to: today };
  }
}

/**
 * The same window, one year earlier — the baseline the closed-months chart
 * draws behind the current series.
 *
 * Shifted by whole years rather than by the window's length, so "2026" compares
 * against "2025" and a rolling six months compares against the same six months
 * a year ago. Comparing against the immediately preceding stretch would put a
 * summer against a winter and call the difference performance.
 *
 * February 29th shifts to the 28th: `Date` would roll it into March 1st, and a
 * window is a range, not a birthday.
 *
 * @param range The window in view.
 */
export function previousYearRange(range: DashboardPeriodRange): DashboardPeriodRange {
  return { from: shiftYear(range.from), to: shiftYear(range.to) };
}

function shiftYear(day: string): string {
  const [year, month, date] = day.split('-');
  const shifted = Number(year) - 1;
  const clamped = month === '02' && date === '29' ? '28' : date;
  return `${shifted}-${month}-${clamped}`;
}

/** The chosen option's label, for a card that reports the window without
 *  owning the control. */
export function dashboardPeriodLabel(value: string, now: Date = new Date()): string {
  const match = dashboardPeriodOptions(now).find((option) => option.value === value);
  return match ? match.label : 'PERIOD_CURRENT_YEAR';
}

/** The least a month has to carry for the window label to place it. */
export interface DashboardWindowMonth {
  year: number;
  month: number;
}

/**
 * Names the window the payload actually covers: "ene – ago 2026", or
 * "sept 2025 – ago 2026" when it straddles two years.
 *
 * Read off the data, never off the chosen period — which is the point. Every
 * card in this block reports a window it does not own, and echoing the
 * selector's own label only ever says what was asked for. This says what came
 * back: if the card reads September through August while the selector says the
 * current year, the server answered a different question.
 *
 * Returns an empty string for an empty payload, which the caller renders as
 * nothing rather than as a range with no ends.
 *
 * @param months The window's months, in order, as the summary returns them.
 * @param lang Active locale, for the month names.
 */
export function dashboardWindowLabel(
  months: readonly DashboardWindowMonth[],
  lang: string
): string {
  if (!months.length) {
    return '';
  }

  const first = months[0];
  const last = months[months.length - 1];
  const start = DateTime.fromObject({ year: first.year, month: first.month }).setLocale(lang);
  const end = DateTime.fromObject({ year: last.year, month: last.month }).setLocale(lang);

  return start.year === end.year
    ? `${start.toFormat('LLL')} – ${end.toFormat('LLL')} ${end.year}`
    : `${start.toFormat('LLL yyyy')} – ${end.toFormat('LLL yyyy')}`;
}
